import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../services/api';

interface LedgerLine {
  entry_number: string;
  entry_date: string;
  entry_desc: string;
  line_desc: string | null;
  debit_amount: number;
  credit_amount: number;
  running_balance: number;
  entry_type: string;
  status: string;
  journal_entry_id: number;
}

interface COAAccount {
  id: number;
  code: string;
  name: string;
  account_type: string;
  normal_balance: string;
  account_group: string;
}

const fmt = (n: number) => (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 });

const formatBalance = (n: number, normal: string) => {
  const abs = Math.abs(n);
  const sign = n < 0 ? (normal === 'debit' ? ' CR' : ' DR') : (normal === 'debit' ? ' DR' : ' CR');
  return `${fmt(abs)}${sign}`;
};

const GeneralLedger: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<COAAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get('/accounting/accounts', { params: { active: 'true' } })
      .then((r) =>
        setAccounts(
          r.data.data?.filter((a: COAAccount) => a.account_type !== undefined) || []
        )
      )
      .catch(() => {});
  }, []);

  const handleLoad = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/accounting/ledger', {
        params: { account_id: selectedAccount, from_date: fromDate, to_date: toDate },
      });
      setLedgerData(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const acct = ledgerData?.account as COAAccount | undefined;

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/accounting')}>← Back</BackButton>
        <div>
          <h1>General Ledger</h1>
          <Subtitle>Complete transaction history for any account with running balance</Subtitle>
        </div>
      </Header>

      <FilterCard>
        <FilterRow>
          <SelectWrapper>
            <Label>Account</Label>
            <Select
              value={selectedAccount}
              onChange={(e) => {
                setSelectedAccount(e.target.value);
                setLedgerData(null);
              }}
            >
              <option value="">-- Select Account --</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
            </Select>
          </SelectWrapper>

          <InputWrapper>
            <Label>From Date</Label>
            <DateInput
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </InputWrapper>

          <InputWrapper>
            <Label>To Date</Label>
            <DateInput
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </InputWrapper>

          <LoadButton onClick={handleLoad} disabled={!selectedAccount || loading}>
            {loading ? 'Loading...' : 'Load Ledger'}
          </LoadButton>

          {ledgerData && (
            <ExportButton onClick={() => window.print()}>Export PDF</ExportButton>
          )}
        </FilterRow>
      </FilterCard>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      {ledgerData && acct && (
        <LedgerCard>
          <AccountHeader>
            <AccountCode>{acct.code}</AccountCode>
            <AccountInfo>
              <AccountName>{acct.name}</AccountName>
              <AccountMeta>
                Type: <strong>{acct.account_type}</strong> | Normal Balance:{' '}
                <strong>{acct.normal_balance}</strong>
              </AccountMeta>
            </AccountInfo>
          </AccountHeader>

          <BalanceSummary>
            <BalanceItem>
              <BalanceLabel>Opening Balance</BalanceLabel>
              <BalanceValue>
                {formatBalance(ledgerData.opening_balance, acct.normal_balance)}
              </BalanceValue>
            </BalanceItem>
            <BalanceItem>
              <BalanceLabel>Total Debits</BalanceLabel>
              <BalanceValue>LKR {fmt(ledgerData.total_debit)}</BalanceValue>
            </BalanceItem>
            <BalanceItem>
              <BalanceLabel>Total Credits</BalanceLabel>
              <BalanceValue>LKR {fmt(ledgerData.total_credit)}</BalanceValue>
            </BalanceItem>
            <BalanceItem highlight>
              <BalanceLabel>Closing Balance</BalanceLabel>
              <BalanceValue>
                {formatBalance(ledgerData.closing_balance, acct.normal_balance)}
              </BalanceValue>
            </BalanceItem>
          </BalanceSummary>

          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <Th>Date</Th>
                  <Th>Entry #</Th>
                  <Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Debit</Th>
                  <Th style={{ textAlign: 'right' }}>Credit</Th>
                  <Th style={{ textAlign: 'right' }}>Balance</Th>
                </TableHeaderRow>
              </thead>
              <tbody>
                {ledgerData.lines.length === 0 ? (
                  <tr>
                    <Td colSpan={6} style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                      No transactions in this period
                    </Td>
                  </tr>
                ) : (
                  ledgerData.lines.map((line: LedgerLine, idx: number) => (
                    <TableRow key={idx}>
                      <Td>{line.entry_date}</Td>
                      <Td>
                        <EntryNumber>{line.entry_number}</EntryNumber>
                      </Td>
                      <Td>
                        <div>{line.entry_desc}</div>
                        {line.line_desc && (
                          <LineDesc>{line.line_desc}</LineDesc>
                        )}
                      </Td>
                      <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {line.debit_amount > 0 ? fmt(line.debit_amount) : '—'}
                      </Td>
                      <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {line.credit_amount > 0 ? fmt(line.credit_amount) : '—'}
                      </Td>
                      <Td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                        {formatBalance(line.running_balance, acct.normal_balance)}
                      </Td>
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </TableContainer>
        </LedgerCard>
      )}

      {!ledgerData && !loading && (
        <EmptyState>
          <EmptyIcon>📖</EmptyIcon>
          <p>Select an account and date range, then click "Load Ledger" to view its transaction history.</p>
        </EmptyState>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin-bottom: 5px;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #667eea;
    color: #667eea;
  }
`;

const FilterCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 300px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #555;
`;

const Select = styled.select`
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const DateInput = styled.input`
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const LoadButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #5568d3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportButton = styled.button`
  padding: 10px 24px;
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #667eea;
    color: white;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c00;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 20px;
`;

const LedgerCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const AccountHeader = styled.div`
  padding: 30px;
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
  color: white;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const AccountCode = styled.div`
  font-size: 32px;
  font-weight: 800;
  font-family: monospace;
  background: rgba(255, 255, 255, 0.2);
  padding: 15px 20px;
  border-radius: 8px;
`;

const AccountInfo = styled.div`
  flex: 1;
`;

const AccountName = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
`;

const AccountMeta = styled.div`
  opacity: 0.9;
  font-size: 14px;

  strong {
    font-weight: 600;
    text-transform: capitalize;
  }
`;

const BalanceSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
`;

const BalanceItem = styled.div<{ highlight?: boolean }>`
  padding: 20px;
  text-align: center;
  background: ${props => (props.highlight ? 'white' : 'transparent')};
  border-right: 1px solid #eee;

  &:last-child {
    border-right: none;
  }
`;

const BalanceLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const BalanceValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  font-family: monospace;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderRow = styled.tr`
  background: #f5f5f5;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #ddd;
`;

const TableRow = styled.tr`
  &:hover {
    background: #f9f9f9;
  }
`;

const Td = styled.td`
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
`;

const EntryNumber = styled.span`
  font-family: monospace;
  color: #667eea;
  font-weight: 600;
`;

const LineDesc = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  font-style: italic;
`;

export default GeneralLedger;
